import { memo, useCallback, useEffect, useState } from "react";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import {
  useHandleDropdownChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";

import { OFFICE_ADDRESS_EDIT_FORM_SCHEMA } from "./validation";

import { OfficeAddressEditProps } from "./types";
import { initialPagination } from "ui/components/Table/consts";
import { CheckBox } from "ui/components/CheckBox";
import { TextArea } from "ui/components/TextField/TextArea";
import { OfficeAddressEditFormValues, OfficeAddressRow } from "services/api/offices/types";
import { initialOfficeAddressEditForm } from "services/api/offices/consts";
import {
  createOfficeAddressAPI,
  getOfficeAddressAPI,
  getOfficeAddressesAPI,
  updateOfficeAddressAPI,
} from "services/api/offices/api";
import { AddressMap } from "ui/components/Map/AddressMap";
import { AddressData, AddressFormData } from "ui/components/Map/AddressMap/types";
import { useTranslate } from "helpers";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { EUROPEAN_COUNTRIES } from "services/api/settings";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";

const OfficeAddressEditCard: React.FC<OfficeAddressEditProps> = (props) => {
  const { addressId, officeId, show, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const { createDialog } = useDialog();

  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [existingAddresses, setExistingAddresses] = useState<OfficeAddressRow[]>([]);
  const [formValues, setFormValues] = useState<OfficeAddressEditFormValues>(
    initialOfficeAddressEditForm
  );

  const [mapPosition, setMapPosition] = useState<{ lat: number; lng: number } | undefined>(
    undefined
  );

  useEffect(() => {
    fetchExistingAddresses();
    fetchAddress();
  }, [addressId]);

  const fetchExistingAddresses = async () => {
    try {
      const result = await getOfficeAddressesAPI({ ...initialPagination, pageSize: 100 }, officeId);
      setExistingAddresses(result.data);
    } catch {
      setExistingAddresses([]);
    }
  };

  const fetchAddress = async () => {
    if (!addressId) {
      setFormValues(initialOfficeAddressEditForm);
      setMapPosition(undefined);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getOfficeAddressAPI(officeId, addressId);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);

    if (result.latitude && result.longitude) {
      setMapPosition({ lat: result.latitude, lng: result.longitude });
    }

    setIsLoading(false);
    setErrors({});
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(formValues, OFFICE_ADDRESS_EDIT_FORM_SCHEMA, setErrors);
    if (!isValid) return;

    if (formValues.primary) {
      const existingPrimaryAddress = existingAddresses.find(
        (address) => address.primary && address.id !== addressId
      );

      if (existingPrimaryAddress) {
        setErrors((prev) => ({
          ...prev,
          primary: `Primarna adresa već postoji: ${existingPrimaryAddress.line1}`,
        }));
        return;
      }
    }

    setIsLoading(true);

    try {
      let result;
      if (!!addressId) {
        result = await updateOfficeAddressAPI(officeId, addressId, formValues);
      } else {
        result = await createOfficeAddressAPI(officeId, formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);

  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);

  const handleCheckBoxChange = useCallback(() => {
    setFormValues((old) => ({ ...old, primary: !old.primary }));
  }, []);

  const handleCloseModal = () => {
    createDialog({
      onConfirm: onClose,
    });
  };

  const handleMapChange = useCallback((data: AddressData) => {
    setFormValues((prev) => ({
      ...prev,
      country: data.country || prev.country,
      city: data.city || prev.city,
      state: data.state || prev.state,
      postalCode: data.postalCode || prev.postalCode,
      line1: data.line1 || prev.line1,
      latitude: data.latitude,
      longitude: data.longitude,
    }));

    if (data.latitude && data.longitude) {
      setMapPosition({ lat: data.latitude, lng: data.longitude });
    }
  }, []);

  const addressFormData: AddressFormData = {
    country: formValues.country ?? undefined,
    city: formValues.city ?? undefined,
    state: formValues.state ?? undefined,
    postalCode: formValues.postalCode ?? undefined,
    line1: formValues.line1 ?? undefined,
    line2: formValues.line2 ?? undefined,
  };

  const content = (
    <>
      <div className="flex  h-[calc(100vh-8rem)] divide-x-2 ">
        <div className="flex-1 min-w-0">
          <AddressMap
            onChange={handleMapChange}
            initialPosition={mapPosition}
            formData={addressFormData}
          />
        </div>
        <div className="xl:w-1/4 lg:w-1/4 md:w-1/3 w-full md:shrink-0 p-4">
          <form noValidate className="text-left h-full flex flex-col">
            <div className="flex-1 min-h-0">
              <div className="flex gap-3 mb-3">
                <div className="flex-1">
                  <Dropdown
                    name="country"
                    label="Država"
                    placeholder="Odaberite državu"
                    onChange={handleDropdownChange}
                    error={errors.country}
                    value={formValues.country}
                    options={EUROPEAN_COUNTRIES}
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3 mb-3">
                <div className="flex-1">
                  <TextField
                    type="text"
                    name="state"
                    label="Županija / Općina"
                    placeholder="Unesite županiju / općinu"
                    onChange={handleTextFieldChange}
                    error={errors.state}
                    value={formValues.state}
                    required
                    endIcon=""
                  ></TextField>
                </div>
              </div>
              <div className="flex gap-3 mb-3">
                <div className="flex-1">
                  <TextField
                    type="text"
                    name="city"
                    label="Grad"
                    placeholder="Unesite grad"
                    onChange={handleTextFieldChange}
                    error={errors.city}
                    value={formValues.city}
                    required
                    endIcon=""
                  ></TextField>
                </div>
              </div>

              <div className="flex gap-3 mb-3">
                <div className="flex-1">
                  <TextField
                    type="text"
                    name="postalCode"
                    label="Poštanski broj"
                    placeholder="Unesite poštanski broj"
                    onChange={handleTextFieldChange}
                    error={errors.postalCode}
                    value={formValues.postalCode}
                    required
                    endIcon=""
                  ></TextField>
                </div>
              </div>
              <div className="flex gap-3 mb-3">
                <div className="flex-1">
                  <TextArea
                    name="line1"
                    label="Adresa 1"
                    placeholder="Unesite adresu"
                    onChange={handleTextFieldChange}
                    error={errors.line1}
                    value={formValues.line1}
                    required
                    endIcon=""
                  ></TextArea>
                </div>
              </div>
              <div className="flex gap-3 mb-3">
                <div className="flex-1">
                  <TextArea
                    name="line2"
                    label="Adresa 2"
                    placeholder="Unesite dodatnu adresu"
                    onChange={handleTextFieldChange}
                    error={errors.line2}
                    value={formValues.line2}
                    required
                    endIcon=""
                  ></TextArea>
                </div>
              </div>

              <div className="flex gap-3 mb-3">
                <div className="flex-1">
                  <TextArea
                    name="description"
                    label="Opis"
                    placeholder="Unesite opis"
                    onChange={handleTextFieldChange}
                    error={errors.description}
                    value={formValues.description}
                    required
                    endIcon=""
                  ></TextArea>
                </div>
              </div>
              <div className="flex gap-3 mb-3">
                <div className="flex-1">
                  <CheckBox
                    name="primary"
                    onChange={handleCheckBoxChange}
                    checked={Boolean(formValues.primary)}
                    label="Primarna"
                    error={errors.primary}
                  ></CheckBox>
                </div>
              </div>
            </div>
            <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
              <Button
                variant="outlined"
                children={t("admin.close")}
                onClick={handleCloseModal}
              />
              <Button
                variant="primary"
                children={t("admin.save")}
                onClick={handleFormSubmit}
              />
            </div>
          </form>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );

  if (show !== undefined) {
    return (
      <Modal show={show} onHide={onClose} position="centered" size="full">
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title={addressId ? "Ažuriranje adrese" : "Unos adrese"}
          onHide={onClose}
        />
        <ModalBody>{show && content}</ModalBody>
      </Modal>
    );
  }

  return content;
};
export default memo(OfficeAddressEditCard);
